import {config} from "../../../config/config";
import 'js-loading-overlay';
import {jsPDF} from "jspdf";
import {saveAs} from "file-saver";
import { Canvas as FabricCanvas, FabricImage } from 'fabric';
import {MapEvent} from "../../document/model/map";
import {mapboxglref} from "@/js/mapboxService";

const zoomLevelScale = [500,1000,
      2000,
      5000,
      10000];

const customWidthId = 'printr-control-custom-width';
const customHeightId = 'print-control-custom-height';

let scaleIndex = 3; // zoomLevelScale = 5000

let cutoutData = {};

type Options = {
  PageSize: any;
  PageOrientation: string;
  Format: string;
  DPI: number;
  Crosshair?: boolean;
  PrintableArea: boolean;
  accessToken?: string;
  Local?: 'de' | 'en' | 'fr' | 'fi' | 'sv' | 'vi';
};

class CrosshairManager {
  private map;
  private width;
  private height;
  private svgCanvas;
  private xLine;
  private yLine;
  private color;

  constructor(map) {
    this.color = '#535353';
    this.map = map;
    this.mapResize = this.mapResize.bind(this);
  }

  create() {
    this.updateValues();
    if (this.map !== undefined) {
      this.map.on('resize', this.mapResize);
      this.createCanvas(this.map.getCanvasContainer());
    } else {
      console.error('map object is null');
    }
  }

  updateValues() {
    var _a, _b;
    this.width = (_a = this.map) === null || _a === void 0 ? void 0 : _a.getCanvas().clientWidth;
    this.height = (_b = this.map) === null || _b === void 0 ? void 0 : _b.getCanvas().clientHeight;
  }

  mapResize() {
    this.updateValues();
    this.updateCanvas();
  }

  updateCanvas() {
    if (this.svgCanvas !== undefined
      && this.yLine !== undefined
      && this.xLine !== undefined
      && this.width !== undefined
      && this.height !== undefined) {
      this.svgCanvas.setAttribute('width', `${this.width}px`);
      this.svgCanvas.setAttribute('height', `${this.height}px`);
      const halfWidth = this.width / 2;
      const halfHeight = this.height / 2;
      this.yLine.setAttribute('x1', `${halfWidth}px`);
      this.yLine.setAttribute('y1', '0px');
      this.yLine.setAttribute('x2', `${halfWidth}px`);
      this.yLine.setAttribute('y2', `${this.height}px`);
      this.xLine.setAttribute('x1', `${0}px`);
      this.xLine.setAttribute('y1', `${halfHeight}px`);
      this.xLine.setAttribute('x2', `${this.width}px`);
      this.xLine.setAttribute('y2', `${halfHeight}px`);
    } else {
      console.error('element value is null');
    }
  }

  createCanvas(container) {
    if (this.width !== undefined
      && this.height !== undefined) {
      const canvas = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      canvas.style.position = 'relative';
      canvas.setAttribute('width', `${this.width}px`);
      canvas.setAttribute('height', `${this.height}px`);
      const halfWidth = this.width / 2;
      const halfHeight = this.height / 2;
      this.yLine = canvas.appendChild(this.createLine(halfWidth, 0, halfWidth, this.height, this.color, '2px'));
      this.xLine = canvas.appendChild(this.createLine(0, halfHeight, this.width, halfHeight, this.color, '2px'));
      container === null || container === void 0 ? void 0 : container.appendChild(canvas);
      this.svgCanvas = canvas;
    }
  }

  createLine(x1, y1, x2, y2, color, w) {
    const aLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    aLine.setAttribute('x1', x1);
    aLine.setAttribute('y1', y1);
    aLine.setAttribute('x2', x2);
    aLine.setAttribute('y2', y2);
    aLine.setAttribute('stroke-dasharray', '5,5');
    aLine.setAttribute('stroke', color);
    aLine.setAttribute('stroke-width', w);
    return aLine;
  }

  destroy() {
    if (this.xLine !== undefined) {
      this.xLine.remove();
      this.xLine = undefined;
    }
    if (this.yLine !== undefined) {
      this.yLine.remove();
      this.yLine = undefined;
    }
    if (this.svgCanvas !== undefined) {
      this.svgCanvas.remove();
      this.svgCanvas = undefined;
    }
    if (this.map !== undefined) {
      this.map?.off('resize', this.mapResize);
      this.map = undefined;
    }
  }
}

class PrintableAreaManager {
  private map;
  private width;
  private height;
  private unit;
  private svgCanvas;
  private svgPath;

  constructor(map) {
    var _a, _b, _c;
    this.map = map;
    if (this.map === undefined) {
      return;
    }
    this.mapResize = this.mapResize.bind(this);
    this.map.on('resize', this.mapResize);
    const clientWidth = (_a = this.map) === null || _a === void 0 ? void 0 : _a.getCanvas().clientWidth;
    const clientHeight = (_b = this.map) === null || _b === void 0 ? void 0 : _b.getCanvas().clientHeight;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.top = '0px';
    svg.style.left = '0px';
    svg.setAttribute('width', `${clientWidth}px`);
    svg.setAttribute('height', `${clientHeight}px`);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('style', 'fill:#888888;stroke-width:0');
    path.setAttribute('fill-opacity', '0.5');
    svg.append(path);
    (_c = this.map) === null || _c === void 0 ? void 0 : _c.getCanvasContainer().appendChild(svg);
    this.svgCanvas = svg;
    this.svgPath = path;
  }

  mapResize() {
    this.generateCutOut();
  }

  updateArea(width, height) {
    this.width = width;
    this.height = height;
    this.unit = this.map.consts.Unit.mm;
    this.generateCutOut();
  }

  generateCutOut() {
    var _a, _b;
    if (this.map === undefined
      || this.svgCanvas === undefined
      || this.svgPath === undefined) {
      return;
    }
    const {width, height} = this.scaleWidthHeight(this.width, this.height);

    const clientWidth = (_a = this.map) === null || _a === void 0 ? void 0 : _a.getCanvas().clientWidth;
    const clientHeight = (_b = this.map) === null || _b === void 0 ? void 0 : _b.getCanvas().clientHeight;
    const startX = clientWidth / 2 - width / 2;
    const endX = startX + width;
    const startY = clientHeight / 2 - height / 2;
    const endY = startY + height;

    cutoutData = {
      startX, startY, endX, endY
    }

    this.svgCanvas.setAttribute('width', `${clientWidth}px`);
    this.svgCanvas.setAttribute('height', `${clientHeight}px`);
    this.svgPath.setAttribute('d', `M 0 0 L ${clientWidth} 0 L ${clientWidth} ${clientHeight} L 0 ${clientHeight} M ${startX} ${startY} L ${startX} ${endY} L ${endX} ${endY} L ${endX} ${startY}`);
  }

  destroy() {
    if (this.svgCanvas !== undefined) {
      this.svgCanvas.remove();
      this.svgCanvas = undefined;
    }
    if (this.map !== undefined) {
      this.map = undefined;
    }

    const zoomEvent = new Event('updatePrintScale');
    document.dispatchEvent(zoomEvent);
  }

  toPixels(length, conversionFactor = 96) {
    if (this.unit === this.map.consts.Unit.mm) {
      conversionFactor /= 25.4;
    }
    return conversionFactor * length;
  }

  scaleWidthHeight(width, height): {width: number, height: number} {
    width = this.toPixels(width);
    height = this.toPixels(height);

    let parentContainer = this.map.getContainer().getBoundingClientRect();

    let widthRatio = parentContainer.width / width;
    let heightRatio = parentContainer.height / height;

    if (widthRatio < 1 || heightRatio < 1) {
      let ratio = widthRatio < heightRatio ? widthRatio : heightRatio;
      width = width * ratio;
      height = height * ratio;
    }

    return {width, height};
  }
}

class MapGenerator {
  private map;
  private width;
  private height;
  private dpi;
  private format;
  private unit;
  private accesstoken;
  public mapboxgl = mapboxglref();


  constructor(map, size = [297, 210], dpi = 96, format = 'png', unit = 'mm', accesstoken) {
    this.map = map;
    this.width = size[0];
    this.height = size[1];
    this.dpi = dpi;
    this.format = format;
    this.unit = unit;
    this.accesstoken = accesstoken;
  }

  generate(canDownload = false, isPrintStamp = false, isPrintStampMapbox=false, pageScale='') {
    const this_ = this;
    JsLoadingOverlay.show({
      overlayBackgroundColor: '#5D5959',
      overlayOpacity: '0.6',
      spinnerIcon: 'ball-spin',
      spinnerColor: '#2400FD',
      spinnerSize: '2x',
      overlayIDName: 'overlay',
      spinnerIDName: 'spinner',
      offsetX: 0,
      offsetY: 0,
      containerID: null,
      lockScroll: false,
      overlayZIndex: 9998,
      spinnerZIndex: 9999,
    });
    const actualPixelRatio = window.devicePixelRatio;
    Object.defineProperty(window, 'devicePixelRatio', {
      get() {
        return this_.dpi / 96;
      },
    });
    const hidden = document.createElement('div');
    hidden.className = 'hidden-map';
    document.body.appendChild(hidden);
    const container = document.createElement('div');
    const width = this.toPixels(this.width, this_.dpi);
    const  height = this.toPixels(this.height, this_.dpi);

    // const { width, height } = this.scaleWidthHeight(this.width, this.height);
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    hidden.appendChild(container);
    const style = this.map.getStyle();
    if (style && style.sources) {
      const sources = style.sources;
      Object.keys(sources).forEach((name) => {
        const src = sources[name];
        Object.keys(src).forEach((key) => {
          if (!src[key])
            delete src[key];
        });
      });
    }
    const renderMap = new this.mapboxgl.Map({
      accessToken: this.accesstoken || this.mapboxgl.accessToken,
      container,
      style,
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
      interactive: false,
      preserveDrawingBuffer: true,
      fadeDuration: 0,
      attributionControl: false,
      transformRequest: this.map._requestManager._transformRequestFn,
    });
    renderMap.once('load', () => {
      const images = (this.map.style.imageManager || {}).images || [];
      const imagePromises = [];

      Object.keys(images).forEach((key) => {
        if (images[key].data && !renderMap.hasImage(key)) {
          renderMap.addImage(key, images[key].data);
        }
      });

      if (this.map.listImages) {
        const loadedImages = this.map.listImages();
        loadedImages.forEach((imageName) => {
          if (!renderMap.hasImage(imageName)) {
            const img = this.map.style.getImage(imageName);
            if (img) {
              renderMap.addImage(imageName, img.data);
            }
          }
        });
      }
    });
    renderMap.once('idle', () => {

      var _a;
      const canvas = renderMap.getCanvas();
      const fileName = `map.${this_.format}`;


      if(isPrintStamp){
        const dataURL = canvas.toDataURL();
        document.dispatchEvent(new CustomEvent("stampPrintEvent", { detail: { dataURL, zoom: renderMap.getZoom() , pageScale: pageScale, bounds: renderMap.getBounds(), size: {
          width: this.width, height: this.height
        }}, }));
      } else if(isPrintStampMapbox){
         const dataURL = canvas.toDataURL();
        document.dispatchEvent(new CustomEvent("stampPrintMapboxEvent", { detail: { dataURL, zoom: renderMap.getZoom() , pageScale: pageScale, bounds: renderMap.getBounds(), size: {
          width: this.width, height: this.height
        }}, }));
      } else {
      switch (this_.format) {
        case "png":
            this_.toPNG(canvas, fileName, canDownload);
          break;
        case "jpg":
          this_.toJPEG(canvas, fileName, canDownload);
          break;
        case "pdf":
          this_.toPDF(renderMap, fileName, canDownload);
          break;
        case "svg":
          this_.toSVG(canvas, fileName, canDownload);
          break;
        case 'blob':
          this_.toBlob(canvas, fileName);
          break;
        default:
          console.error(`Invalid file format: ${this_.format}`);
          break;
        }
      }
      renderMap.remove();
      (_a = hidden.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(hidden);
      Object.defineProperty(window, 'devicePixelRatio', {
        get() {
          return actualPixelRatio;
        },
      });
      JsLoadingOverlay.hide();
    });
  }

  toBlob(canvas, fileName) {
    canvas.toBlob((blob) => {
      document.dispatchEvent(new CustomEvent("printEvent", {detail: blob}));
      this.map.fire(MapEvent.Print, {detail: blob});
    });
  }

  toPNG(canvas, fileName, canDownload = false, isPrintStamp = false, pageScale = '') {
    canvas.toBlob((blob) => {
      if (canDownload) {
        (0, saveAs)(blob, fileName);
      }
      document.dispatchEvent(new CustomEvent("printEvent", { detail: blob }));

      this.map.fire(MapEvent.Print, { detail: blob });
    });
  }

  toJPEG(canvas, fileName, canDownload = false) {
    const uri = canvas.toDataURL("image/jpeg", 0.85);
    if (canDownload) {
      const a = document.createElement("a");
      a.href = uri;
      a.download = fileName;
      a.click();
      a.remove();
    }

    document.dispatchEvent(new CustomEvent("printEvent", {detail: uri}));
    this.map.fire(MapEvent.Print, {detail: uri});
  }

  toPDF(map, fileName, canDownload = false) {
    const canvas = map.getCanvas();
    const pdf = new jsPDF({
      orientation: this.width > this.height ? "l" : "p",
      unit: this.unit,
      compress: true,
    });
    pdf.addImage(canvas.toDataURL("image/png"), "png", 0, 0, this.width, this.height, undefined, "FAST");
    const { lng, lat } = map.getCenter();
    pdf.setProperties({
      title: map.getStyle().name,
      subject: `center: [${lng}, ${lat}], zoom: ${map.getZoom()}`,
      creator: "Mapbox GL Export Plugin",
      author: "(c)Mapbox, (c)OpenStreetMap",
    });

    if (canDownload) {
      pdf.save(fileName);
    }
    document.dispatchEvent(new CustomEvent("printEvent", { detail: pdf }));
    this.map.fire(MapEvent.Print, { detail: pdf });
  }

  toSVG(canvas, fileName, canDownload = false) {
    const uri = canvas.toDataURL("image/png");
    FabricImage.fromURL(uri).then((image) => {
      const tmpCanvas = new FabricCanvas("canvas");
      const { width, height } = this.scaleWidthHeight(this.width, this.height);
      image.scaleToWidth(width);
      image.scaleToHeight(height);
      tmpCanvas.add(image);
      const svg = tmpCanvas.toSVG({
        x: 0,
        y: 0,
        width: width,
        height: height,
        viewBox: {
          x: 0,
          y: 0,
          width: width,
          height: height,
        },
      });
      if (canDownload) {
        const a = document.createElement("a");
        a.href = `data:application/xml,${encodeURIComponent(svg)}`;
        a.download = fileName;
        a.click();
        a.remove();
      }

      document.dispatchEvent(new CustomEvent("printEvent", { detail: svg }));
      this.map.fire(MapEvent.Print, { detail: svg });
    });
  }

  toPixels(length, conversionFactor = 96) {
    if (this.unit === 'mm') {
      conversionFactor /= 25.4;
    }
    return conversionFactor * length;
  }

  scaleWidthHeight(width, height, dpi = undefined): { width: number, height: number } {
    width = this.toPixels(width, dpi);
    height = this.toPixels(height, dpi);

    let parentContainer = this.map.getContainer().getBoundingClientRect();

    let widthRatio = parentContainer.width / width, heightRatio = parentContainer.height / height;
    if (widthRatio < 1 || heightRatio < 1) {
      let ratio = widthRatio < heightRatio ? widthRatio : heightRatio;
      width = width * ratio;
      height = height * ratio;
    }

    return { width, height };
  }
}

export default class PrintControl implements mapboxgl.IControl {
  private controlContainer;
  private exportContainer;
  private crosshair;
  private printableArea;
  private map?;
  private exportButton;
  private options;

  private lastScale;

  public Format = {
    JPEG: "jpg",
    PNG: "png",
    PDF: "pdf",
    SVG: "svg",
    Blob: 'blob'
  };
  public Unit = {
    in: "in",
    mm: "mm",
  };
  //type Unit = typeof Unit[keyof typeof Unit];
  public Size = {
    A4: [297, 210],
    A0: [1189, 841],
    A1: [841, 594],
    A2: [594, 420],
    A3: [420, 297],
    A5: [210, 148],
    A6: [148, 105],
    Personalizat: [0, 0],
  };

  public Scale = {
    2000: 21,
    5000: 19,
    10000: 18
  }

  //type Size = typeof Size[keyof typeof Size];
  public PageOrientation = {
    Landscape: "landscape",
    Portrait: "portrait",
  };
  public DPI = {
    // 72: 72,
    96: 96,
    // 200: 200,
    // 300: 300,
    // 400: 400,
  };

  public isCustom = false;

  constructor(options: Options) {
    this.options = {
      PageSize: [297, 210],
      PageOrientation: "portrait",
      Format: "png",
      DPI: 300,
      Crosshair: false,
      PrintableArea: true,
      accessToken: config.MAPBOX_TOKEN,
      PageScale: 2000
    };
    if (options) {
      this.options = Object.assign(this.options, options);
    }
    this.onDocumentClick = this.onDocumentClick.bind(this);

    document.removeEventListener("updatePrintData", this.updatePrintData);
    document.addEventListener("updatePrintData", this.updatePrintData);
  }

  updatePrintData(e){

      const customWidthInput: HTMLElement | null = document.getElementById("mapbox-gl-export-width");
      const customHeightInput: HTMLElement | null = document.getElementById("mapbox-gl-export-height");


      const bbox = e.bbox;


      const averageTestedScale = 3.75;
      (customWidthInput as HTMLInputElement).value = String(Math.abs((bbox[1].x - bbox[0].x) / averageTestedScale));
      (customHeightInput as HTMLInputElement).value = String(Math.abs((bbox[1].y - bbox[0].y) / averageTestedScale));


      this.updatePrintableArea();

  }

  onAdd(map) {

    this.map = map;
    this.map.consts = {
      Format: this.Format,
      Unit: this.Unit,
      Size: this.Size,
      DPI: this.DPI
    }
    this.controlContainer = document.createElement('div');
    this.controlContainer.classList.add('mapboxgl-ctrl');
    this.controlContainer.classList.add('mapboxgl-ctrl-group');

    this.exportContainer = document.createElement('div');
    this.exportContainer.classList.add('mapboxgl-export-list');

    this.exportButton = document.createElement('button');
    // this.exportButton.classList.add('mapboxgl-ctrl-icon');
    this.exportButton.classList.add('mapboxgl-export-control');
    this.exportButton.type = 'button';
    this.exportButton.addEventListener('click', () => {
      this.exportButton.style.display = 'none';
      this.exportContainer.style.display = 'block';
      this.toggleCrosshair(true);
      this.togglePrintableArea(true);
    });

    let icon = document.createElement('div');
    icon.classList.add('mapboxgl-ctrl-icon');
    this.exportButton.appendChild(icon);

    document.addEventListener('click', this.onDocumentClick);
    this.controlContainer.appendChild(this.exportButton);
    this.controlContainer.appendChild(this.exportContainer);
    const table = document.createElement('TABLE');
    table.className = 'print-table';
    const tr1 = this.createSelection(this.Size, this.options.translator("Page size"), 'page-size', this.options.PageSize, (data, key) => JSON.stringify(data[key]));
    table.appendChild(tr1);

    const tr11 = this.createInputSelector("Scară 1 la", 'page-scale', "5000");
    if(!this.options.isNoScaleOnPrint){
      table.appendChild(tr11);
    }

    const tr2 = this.createSelection(
      this.PageOrientation,
      this.options.translator("Page orientation"),
      "page-orientaiton",
      this.options.PageOrientation,
      (data, key) => data[key]
    );
    table.appendChild(tr2);
    const tr3 = this.createSelection(
      this.Format,
      "Format",
      "format-type",
      this.options.Format,
      (data, key) => data[key]
    );
    table.appendChild(tr3);
    const tr4 = this.createSelection(this.DPI, "DPI", "dpi-type", this.options.DPI, (data, key) => data[key]);
    table.appendChild(tr4);

    const tr5 = this.createInput(this.options.translator("Width"), "width", "");
    tr5.id = customWidthId
    table.appendChild(tr5);

    const tr6 = this.createInput(this.options.translator("Height"), "height", "");
    tr6.id = customHeightId;
    table.appendChild(tr6);

    this.exportContainer.appendChild(table);

    const closeFunction = ()=>{
      //if custom print
      if (this.isCustom === true) {
        //reset selector
        const pageSize: any = document.getElementById('mapbox-gl-export-page-size');
        pageSize.options[0].selected = true;

        //cancel print
        this.isCustom = false;

        //hide buttons
        const printTable = document.getElementsByClassName('print-table')[0];

        const customWidthInput = document.getElementById(customWidthId);
        const customHeightInput = document.getElementById(customHeightId);

        if (printTable && customWidthInput && customHeightInput) {
          (customWidthInput as any).style.display = 'none';
          (customHeightInput as any).style.display = 'none';
        }

        //switch map selector back to select
        const event = new Event('changeMapMode');
        event.isCustomPrint = false;
        document.dispatchEvent(event);

        const boundingEvent = new Event('updateBoundingBoxMode');
        boundingEvent.isCustomPrint = false;
        document.dispatchEvent(boundingEvent);

        //reset zoom
        const zoomEvent = new Event('updatePrintScale');
        document.dispatchEvent(zoomEvent);
      }

      //hide print-controll
      this.lastScale = undefined;

      this.exportContainer.style.display = 'none';
      this.exportButton.style.display = 'block';
      this.toggleCrosshair(false);
      this.togglePrintableArea(false);
    }

    const closeButton = document.createElement('button');
    closeButton.id = 'closeGenerateBtn';
    closeButton.type = 'button';
    closeButton.textContent = this.options.translator("Close");
    closeButton.classList.add('generate-button');
    closeButton.addEventListener('click', closeFunction);

    const generateButton = document.createElement("button");
    generateButton.id = "exportGenerateBtn";
    generateButton.type = "button";
    generateButton.textContent = this.options.translator("Generate");
    generateButton.classList.add("generate-button");

    const downloadButton = document.createElement("button");
    downloadButton.id = "downloadGenerateBtn";
    downloadButton.type = "button";
    downloadButton.textContent = this.options.translator("Download");
    downloadButton.classList.add("generate-button");

    const printStampButton = document.createElement("button");
    printStampButton.id = "printStampBtn";
    printStampButton.type = "button";
    printStampButton.textContent = this.options.translator("Print stamp");
    printStampButton.classList.add("generate-button");

    const printStampMapboxButton = document.createElement("button");
    printStampMapboxButton.id = "printStampMapboxBtn";
    printStampMapboxButton.type = "button";
    printStampMapboxButton.textContent = this.options.translator("Print stamp Mapbox");
    printStampMapboxButton.classList.add("generate-button");

    const generateFunction = (canDownload = false, isPrintStamp = false, isPrintStampMapbox=false) => {
      const pageSize: any = document.getElementById("mapbox-gl-export-page-size");
      const pageOrientation: any = document.getElementById("mapbox-gl-export-page-orientaiton");
      const formatType: any = document.getElementById("mapbox-gl-export-format-type");
      const dpiType: any = document.getElementById("mapbox-gl-export-dpi-type");

      const customWidthInput: HTMLElement | null = document.getElementById("mapbox-gl-export-width");
      const customHeightInput: HTMLElement | null = document.getElementById("mapbox-gl-export-height");

      const orientValue = pageOrientation.value;
      let pageSizeValue = JSON.parse(pageSize.value);

      if (customWidthInput && customHeightInput) {
        const customWidth = Number((customWidthInput as HTMLInputElement).value);
        const customHeight = Number((customHeightInput as HTMLInputElement).value);

        if (this.checkIfNumber(customWidth) && this.checkIfNumber(customHeight)) {
          pageSizeValue = [customHeight, customWidth];
        }
      }
      if (orientValue === this.PageOrientation.Portrait) {
        pageSizeValue = pageSizeValue.reverse();
      }
      const mapGenerator = new MapGenerator(
        map,
        pageSizeValue,
        Number(dpiType.value),
        formatType.value,
        this.Unit.mm,
        this.options.accessToken
      );

      let pageScale: any = document.getElementById('mapbox-gl-export-page-scale');
      pageScale = pageScale?.value;

      setTimeout(()=>{
        if(isPrintStamp || isPrintStampMapbox){
          closeFunction();
        }
      }, 1500);


      mapGenerator.generate(canDownload, isPrintStamp, isPrintStampMapbox, pageScale);
    };

    generateButton.addEventListener("click", () => generateFunction());
    downloadButton.addEventListener("click", () => generateFunction(true));
    printStampButton.addEventListener("click", () => generateFunction(false, true))
    printStampMapboxButton.addEventListener("click", () => generateFunction(false, false, true))

    this.exportContainer.appendChild(generateButton);
    this.exportContainer.appendChild(downloadButton);
    console.log(this.options);
    if (this.options.isPrintStamp) {
      this.exportContainer.appendChild(printStampButton);
    }
    if (this.options.isPrintStampMapbox) {
      this.exportContainer.appendChild(printStampMapboxButton);
    }
    this.exportContainer.appendChild(closeButton);


    return this.controlContainer;
  }

  createSelection(data, title, type, defaultValue, converter) {
    const label = document.createElement("label");
    label.textContent = title;
    const content = document.createElement("select");
    content.setAttribute("id", `mapbox-gl-export-${type}`);
    content.style.width = "100%";
    Object.keys(data).forEach((key) => {
      const optionLayout = document.createElement("option");
      optionLayout.setAttribute("value", converter(data, key));
      optionLayout.appendChild(document.createTextNode(key));
      optionLayout.setAttribute("name", type);
      if (defaultValue === data[key]) {
        optionLayout.selected = true;
      }
      content.appendChild(optionLayout);
    });
    content.removeEventListener("change", ()=>{this.updatePrintableArea()});
    content.addEventListener("change",()=>{ this.updatePrintableArea()});
    const tr1 = document.createElement("TR");
    const tdLabel = document.createElement("TD");
    const tdContent = document.createElement("TD");
    tdLabel.appendChild(label);
    tdContent.appendChild(content);
    tr1.appendChild(tdLabel);
    tr1.appendChild(tdContent);

    return tr1;
  }

  createInputSelector(title: string, type: string, defaultValue: string){
   //create title element
    const label = document.createElement("label");
    label.textContent = title;

    //create content element
    const content = document.createElement("input");
    const downButton = document.createElement("button");
    downButton.innerHTML = "&#9660;"
    downButton.style.width = "45px";
    downButton.addEventListener("click", () => {
      if(scaleIndex>0){
        scaleIndex --;
      }

      const input = document.getElementById(`mapbox-gl-export-${type}`);

      if(input){
        input.value = zoomLevelScale[scaleIndex];
      }

      this.updatePrintableArea();

    });
    const upButton = document.createElement("button");
    upButton.innerHTML = "&#9650;"
    upButton.style.width = "45px";
    upButton.addEventListener("click", () => {
      if(scaleIndex<4){
        scaleIndex ++;
      }

      const input = document.getElementById(`mapbox-gl-export-${type}`);

      if(input){
        input.value = zoomLevelScale[scaleIndex];
      }

      this.updatePrintableArea();

    });

    content.setAttribute("id", `mapbox-gl-export-${type}`);
    content.style.width = "100%";
    content.setAttribute("type", "number");
    content.setAttribute("min", "0");
    content.setAttribute("oninput", "validity.valid||(value='');");
    content.disabled = true;

     if(defaultValue){
      content.value = defaultValue;
    }

    //set event listener on change input size
    content.addEventListener("change", () => {
      this.updatePrintableArea();
    });

    const tr1 = document.createElement("TR");
    const tdLabel = document.createElement("TD");
    const tdContent = document.createElement("TD");
    tdContent.style.display = 'flex'
    tdLabel.appendChild(label);
    tdContent.appendChild(downButton);
    tdContent.appendChild(content);
    tdContent.appendChild(upButton);
    tr1.appendChild(tdLabel);
    tr1.appendChild(tdContent);

    // tr1.style.display = "none";

    return tr1;
  }

  //add input to view
  createInput(title: string, type: string, defaultValue: string, display: boolean = false, isDisabled = false, isLimit = false) {
    //create title element
    const label = document.createElement("label");
    label.textContent = title;

    //create content element
    const content = document.createElement("input");


    content.setAttribute("id", `mapbox-gl-export-${type}`);
    content.style.width = "100%";
    content.setAttribute("type", "number");
    content.setAttribute("min", "0");
    content.setAttribute("oninput", "validity.valid||(value='');");
    content.disabled = isDisabled;

     if (isLimit) {
      content.min = 0;
      content.max = 22;
    }

     if(defaultValue){
      content.value = 5000;
    }

    //set event listener on change input size
    content.addEventListener("change", () => {
      this.updatePrintableArea();
    });

    const tr1 = document.createElement("TR");
    const tdLabel = document.createElement("TD");
    const tdContent = document.createElement("TD");
    tdLabel.appendChild(label);
    tdContent.appendChild(content);
    tr1.appendChild(tdLabel);
    tr1.appendChild(tdContent);

    if (!display) {
      tr1.style.display = "none";
    }
    return tr1;
  }


  checkIfNumber(variable: number) {
    //check if variable is defined
    //check if its a number
    //check if its a positive number
    return variable && variable === variable && variable > 0;
  }

  onRemove() {
    if (!this.controlContainer || !this.controlContainer.parentNode || !this.map || !this.exportButton) {
      return;
    }
    this.exportButton.removeEventListener("click", this.onDocumentClick);
    this.controlContainer.parentNode.removeChild(this.controlContainer);
    document.removeEventListener("click", this.onDocumentClick);
    if (this.crosshair !== undefined) {
      this.crosshair.destroy();
      this.crosshair = undefined;
    }
    this.map = undefined;
  }

  onDocumentClick(event) {
    if (
      this.controlContainer &&
      !this.controlContainer.contains(event.target) &&
      this.exportContainer &&
      this.exportButton &&
      !this.isCustom
    ) {
      this.exportContainer.style.display = 'none';
      this.exportButton.style.display = 'block';
      this.toggleCrosshair(false);
      this.togglePrintableArea(false);
    }
  }

  toggleCrosshair(state) {
    if (this.options.Crosshair === true) {
      if (state === false) {
        if (this.crosshair !== undefined) {
          this.crosshair.destroy();
          this.crosshair = undefined;
        }
      } else {
        this.crosshair = new CrosshairManager(this.map);
        this.crosshair.create();
      }
    }
  }

  togglePrintableArea(state) {
    if (this.options.PrintableArea === true) {
      this.lastScale = undefined;

      if (state === false) {
        if (this.printableArea !== undefined) {
          this.printableArea.destroy();
          this.printableArea = undefined;
        }


      } else {
        this.printableArea = new PrintableAreaManager(this.map);
        this.updatePrintableArea();
      }
    }
  }

  scaleToZoom(scale ) {
    const scaleToPixel = {
      500: 18.3,
      1000: 17.8,
      2000: 16.45,
      5000: 15.45 ,
      10000: 14.28
    }


    return scaleToPixel[scale];
}

  updatePrintableArea() {
    if (this.printableArea === undefined) {
      return;
    }

    const pageScale: any = document.getElementById('mapbox-gl-export-page-scale');
    const pageStep: any = document.getElementById('mapbox-gl-export-page-step');

    //check if new pagecale is selected
    if (pageScale?.value != this.lastScale && pageScale) {
      const metricValue = pageScale.value ;

      console.log(metricValue);
      this.lastScale = this.scaleToZoom(metricValue)
      console.log(this.lastScale);

      const event = new Event('updatePrintScale');
      event.zoom = this.lastScale || 15.45;
      document.dispatchEvent(event);
    }

    const pageSize: any = document.getElementById('mapbox-gl-export-page-size');
    const pageOrientation: any = document.getElementById('mapbox-gl-export-page-orientaiton');

    //custom size inputs
    const customWidthInput: HTMLElement | null = document.getElementById(customWidthId);
    const customHeightInput: HTMLElement | null = document.getElementById(customHeightId);

    //print table
    const printTable = document.getElementsByClassName('print-table')[0];
    //if custom select show/hide inputs
    if (pageSize.selectedOptions[0].text === 'Personalizat') {
      if (this.isCustom === false) {
        this.isCustom = true;

        //update bounding box select on map
        const event = new Event('changeMapMode');
        event.isCustomPrint = true;
        document.dispatchEvent(event);

        const boundingEvent = new Event('updateBoundingBoxMode');
        boundingEvent.isCustomPrint = true;
        document.dispatchEvent(boundingEvent);

        if (printTable && customWidthInput && customHeightInput) {
          (customWidthInput as any).style.display = 'table-row';
          (customHeightInput as any).style.display = 'table-row';
        }
      }
    } else {
      //if we change from custom to something else
      //undo custom
      if (this.isCustom === true) {
        //update default select on map
        const event = new Event('changeMapMode');
        event.isCustomPrint = false;
        document.dispatchEvent(event);

        const boundingEvent = new Event('updateBoundingBoxMode');
        boundingEvent.isCustomPrint = false;
        document.dispatchEvent(boundingEvent);


        //clear inputs
        if (customWidthInput && customHeightInput) {
          (customWidthInput as HTMLInputElement).value = '';
          (customHeightInput as HTMLInputElement).value = '';
        }

        //hide inputs
        if (printTable) {
          (customWidthInput as any).style.display = 'none';
          (customHeightInput as any).style.display = 'none';
        }

        this.isCustom = false;
      }
    }

    const orientValue = pageOrientation.value;
    let pageSizeValue = JSON.parse(pageSize.value);

    if (orientValue === this.PageOrientation.Portrait) {
      pageSizeValue = pageSizeValue.reverse();
    }

    if (customWidthInput && customHeightInput) {
      const customWidth = Number((customWidthInput as HTMLInputElement).value);
      const customHeight = Number((customHeightInput as HTMLInputElement).value);

      if (this.checkIfNumber(customWidth) && this.checkIfNumber(customHeight) && this.isCustom === true) {
        this.printableArea.updateArea(customWidth, customHeight);
      } else {
        this.printableArea.updateArea(pageSizeValue[0], pageSizeValue[1]);
      }
    }
  }
}
export { };